import { useEffect, useState } from 'react';

import {
  useComensalSession,
  type UseComensalSessionReturn,
} from '@/hooks/useComensalSession';
import type { ComensalSessionLabels } from '@/i18n/comensalSession';

interface Props {
  sessionToken: string | null;
  entryToken?: string | null;
  isAvailable: boolean;
  labels: ComensalSessionLabels;
  state?: UseComensalSessionReturn;
}

/**
 * 메뉴와 결제 딥링크가 함께 쓰는 좌석 합류 경계.
 *
 * 분할 비활성 매장에서는 완전히 사라지고, 활성 매장에서는 서버의 join_strategy에 따라
 * 코드 입력 또는 POS 직원 승인을 기다리는 상태를 보여준다.
 */
export function ComensalAccessPanel({
  sessionToken,
  entryToken = null,
  isAvailable,
  labels,
  state,
}: Props) {
  const scanToken = isAvailable ? sessionToken : null;
  const localState = useComensalSession(
    state === undefined ? scanToken : null,
    state === undefined && isAvailable ? entryToken : null,
  );
  const {
    deviceToken,
    comensal,
    isScanning,
    error,
    scan,
    join,
    refreshIdentity,
    ensureIdentity,
  } = state ?? localState;
  const [displayName, setDisplayName] = useState('');
  const [joinCode, setJoinCode] = useState('');

  useEffect(() => {
    if (isAvailable && deviceToken !== null) void ensureIdentity();
  }, [isAvailable, deviceToken, ensureIdentity]);

  // 대표 승인을 기다리는 동안 새로고침 없이 상태가 풀리도록 서버 권위를 다시 읽는다.
  useEffect(() => {
    if (comensal?.join_status !== 'pending') return;

    const timer = window.setInterval(() => { void refreshIdentity(); }, 5000);
    return () => { window.clearInterval(timer); };
  }, [comensal?.join_status, refreshIdentity]);

  if (!isAvailable || (sessionToken === null && entryToken === null)) return null;

  const shouldShowAccess = comensal === null || comensal.join_status !== 'joined';

  const handleSaveName = (): void => {
    const name = displayName.trim();
    if (name !== '') void scan(name);
  };

  const handleJoin = (): void => {
    if (/^\d{4}$/.test(joinCode)) {
      void join({ code: joinCode });
    }
  };

  return (
    <div className="space-y-3" data-testid="comensal-access-boundary">
      {shouldShowAccess ? (
        <section
          className="rounded-lg border border-primary/30 bg-background p-4"
          data-testid="comensal-access-panel"
          aria-live="polite"
        >
          <h3 className="text-sm font-semibold text-foreground">{labels.title}</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{labels.browseOnly}</p>

          {isScanning && comensal === null ? (
            <p className="mt-3 text-sm text-muted-foreground" role="status">
              {labels.scanning}
            </p>
          ) : null}

          {comensal === null && !isScanning ? (
            <label className="mt-3 block text-xs font-medium text-foreground">
              {labels.nameLabel}
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  maxLength={60}
                  value={displayName}
                  onChange={(event) => { setDisplayName(event.target.value); }}
                  placeholder={labels.namePlaceholder}
                  className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                  data-testid="comensal-display-name"
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={displayName.trim() === ''}
                  className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
                  data-testid="comensal-scan-submit"
                >
                  {labels.saveName}
                </button>
              </div>
            </label>
          ) : null}

          {comensal !== null ? (
            <div className="mt-3 space-y-3">
              <p className="text-sm font-medium text-foreground">{comensal.display_name}</p>

              {comensal.join_status === 'rejected' ? (
                <p className="text-sm text-destructive" role="alert">{labels.rejected}</p>
              ) : null}

              {comensal.join_status === 'pending' ? (
                <>
                  <p className="text-sm text-muted-foreground" data-testid="comensal-join-pending">
                    {comensal.session?.join_strategy === 'host_approval'
                      ? labels.pendingHost
                      : labels.pendingCode}
                  </p>

                  {comensal.session?.join_strategy !== 'host_approval' ? (
                    <label className="block text-xs font-medium text-foreground">
                      {labels.codeLabel}
                      <div className="mt-1 flex gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={4}
                          value={joinCode}
                          onChange={(event) => {
                            setJoinCode(event.target.value.replace(/\D/g, '').slice(0, 4));
                          }}
                          placeholder={labels.codePlaceholder}
                          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                          data-testid="comensal-join-code"
                        />
                        <button
                          type="button"
                          onClick={handleJoin}
                          disabled={!/^\d{4}$/.test(joinCode) || isScanning}
                          className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
                          data-testid="comensal-join-submit"
                        >
                          {labels.join}
                        </button>
                      </div>
                    </label>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          {error !== null ? (
            <div className="mt-3" role="alert">
              <button
                type="button"
                onClick={() => {
                  if (deviceToken === null) void scan(displayName.trim() || undefined);
                  else void refreshIdentity();
                }}
                className="text-sm font-medium text-primary underline"
              >
                {labels.retry}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
